import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Client {
  id: string;
  name: string;
  email: string;
  company: string;
  totalProjects: number;
  totalRevenue: string;
  status: 'active' | 'inactive';
}

const MOCK_CLIENTS: Client[] = [
  { id: '1', name: 'Sarah Chen', email: 'sarah@horizonfilms.com', company: 'Horizon Films', totalProjects: 5, totalRevenue: '$12,400', status: 'active' },
  { id: '2', name: 'Marcus Rivera', email: 'marcus@vertexprod.com', company: 'Vertex Productions', totalProjects: 3, totalRevenue: '$8,200', status: 'active' },
  { id: '3', name: 'Emily Watson', email: 'emily@bloommedia.co', company: 'Bloom Media', totalProjects: 2, totalRevenue: '$4,800', status: 'active' },
  { id: '4', name: 'Jake Thornton', email: 'jake@crestline.io', company: 'Crestline Studios', totalProjects: 1, totalRevenue: '$2,500', status: 'inactive' },
];

export function ClientsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const filteredClients = MOCK_CLIENTS.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Clients</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Manage your filmmaking clients and their projects.
          </p>
        </div>
        <button
          style={{
            padding: '10px 20px',
            background: 'var(--color-primary)',
            color: '#FFFDF9',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          + Add Client
        </button>
      </div>

      {/* Search and filter bar */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}
      >
        <input
          type="text"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '10px 14px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: '0.875rem',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          style={{
            padding: '10px 14px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: '0.875rem',
          }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Clients table */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              {['Name', 'Company', 'Projects', 'Revenue', 'Status'].map((header) => (
                <th
                  key={header}
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr
                key={client.id}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                }}
              >
                <td style={{ padding: '14px 16px' }}>
                  <Link
                    to={`/clients/${client.id}`}
                    style={{ color: 'var(--color-text)', fontWeight: 500, textDecoration: 'none' }}
                  >
                    {client.name}
                  </Link>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    {client.email}
                  </div>
                </td>
                <td style={{ padding: '14px 16px', fontSize: '0.875rem' }}>{client.company}</td>
                <td style={{ padding: '14px 16px', fontSize: '0.875rem' }}>{client.totalProjects}</td>
                <td style={{ padding: '14px 16px', fontSize: '0.875rem', fontWeight: 500 }}>{client.totalRevenue}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      background: client.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(168,160,154,0.15)',
                      color: client.status === 'active' ? 'var(--color-success)' : 'var(--color-text-muted)',
                    }}
                  >
                    {client.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredClients.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No clients match your search criteria.
          </div>
        )}
      </div>
    </div>
  );
}
